/**
 * Determine if the current window size matches a given breakpoint
 * Includes catch for server-side rendering
 * @param {string} mq - media query string, e.g. '(min-width: 64rem)'
 * @returns {boolean}
 */
var checkSizeMatch = function checkSizeMatch(mq) {
  // for server-side-rendered JS, skip this
  if (typeof window === 'undefined') {
    return false;
  }

  return window.matchMedia(mq).matches;
};
/**
 * @typedef {Object} AdExperimentContext
 * @property {boolean} isBPage - is the page an article (single post)
 * @property {boolean} isFeedPage - is the page a feed (list of posts)
 * @property {boolean} isBFO - is the page a buzzfeed.com page
 * @property {boolean} isBFN - is the page a buzzfeednews.com page
 * @property {string} localizationCountry - page language country code in format 'en-us' (language-country)
 * @property {string} edition - page edition country code in format 'en-us' (language-country)
 * @property {function} isAdPost - returns boolean as to whether or not post is a branded/sponsored post
 */

/**
 * NOTE: The output of this function will be loaded on various xpager services (e.g. feedpager, bpager etc.).
 * Ensure your isEligible functions will fail safe!
 * @param {AdExperimentContext} context - information about the page used for determining eligibility
 */


var getAdTests = function getAdTests(context) {
  return [
  /***** Permanent feature flags *****/

  /*
   * Please make sure to update the doc as you add/remove feature flags here
   * https://buzzfeed.atlassian.net/wiki/spaces/ADS/pages/302022731/Ads+feature+flags
   */
  {
    name: 'ads_toolbar_bpages',
    variations: ['off', 'on'],
    isEligible: function isEligible() {
      return context.isBPage && checkSizeMatch('(min-width: 52rem)');
    }
  }, {
    name: 'ads_toolbar_feeds',
    variations: ['off', 'on'],
    isEligible: function isEligible() {
      return context.isFeedPage && checkSizeMatch('(min-width: 52rem)');
    }
  }, {
    name: 'ads_toolbar_tasty',
    variations: ['off', 'on'],
    isEligible: function isEligible() {
      return (context === null || context === void 0 ? void 0 : context.destination) === 'tasty' && checkSizeMatch('(min-width: 52rem)');
    }
  }, {
    name: 'ads_prebid',
    variations: ['off', 'on'],
    isEligible: function isEligible() {
      return true;
    }
  }, {
    name: 'ads_bid_cache',
    variations: ['off', 'on'],
    isEligible: function isEligible() {
      return true;
    }
  }, {
    name: 'ads_amazon_tam',
    variations: ['off', 'on'],
    isEligible: function isEligible() {
      return true;
    }
  }, {
    name: 'ads_ad_lightning',
    variations: ['off', 'on'],
    isEligible: function isEligible() {
      return true;
    }
  }, {
    name: 'ads_doubleverify',
    variations: ['off', 'on'],
    isEligible: function isEligible() {
      return true;
    }
  }, {
    name: 'ads_doubleverify_refresh',
    variations: ['off', 'on'],
    isEligible: function isEligible() {
      return true;
    }
  }, {
    name: 'ads_blockthrough',
    variations: ['off', 'on'],
    isEligible: function isEligible() {
      return true;
    }
  },
  /***** MISC AD TESTS *****/
  {
    name: 'advertise_international',
    variations: ['off', 'on'],
    isEligible: function isEligible() {
      // https://buzzfeed.atlassian.net/browse/SITE-5417
      var hiddenEditions = ['pt-br', 'es', 'ja-jp', 'es-mx', 'es-es'];
      return !hiddenEditions.includes(context.edition);
    }
  }, {
    name: 'non_us_ad_lookahead_adjustments',
    variations: ['control', 'on'],
    isEligible: function isEligible() {
      return true;
    }
  }, {
    name: 'ADSGROUP-442-permutive',
    variations: ['off', 'on'],
    isEligible: function isEligible() {
      return true;
    }
  }, {
    name: 'ADSGROUP-143_new_ad_calls_structure',
    variations: ['off', 'on'],
    isEligible: function isEligible() {
      return true;
    }
  }, {
    name: 'ADS-1141-prefetch3',
    variations: ['control', 'early', 'late'],
    isEligible: function isEligible() {
      return !checkSizeMatch('(min-width: 52rem)');
    } // matches solid 'xs' or 'sm' breakpoints

  }, {
    name: 'ads_tam_hem',
    variations: ['off', 'on'],
    isEligible: function isEligible() {
      return true;
    }
  },
  /**
   * Feature flag for bpager-next only
   * ON - GPT lazy loading on desktop, `IntersectionObserver`-based lazy loading on mobile web
   * OFF/not eligible - *both at once* on desktop, `IntersectionObserver` on mobile web
   */
  {
    name: 'ADS-1791-new-bpage-gpt-lazyload',
    variations: ['off', 'on'],
    isEligible: function isEligible() {
      return context.isNewBPage;
    }
  }];
};

export { getAdTests };
